import axios from 'axios';
import Cookies from 'js-cookie';

import {
  AUTH_SERVER_UNAVAILABLE,
  AUTHENTICATE_BY_COOKIE_ENDPOINT,
  BY_HELP_AUTH,
  DEV_BY_HELP_AUTH,
  POST,
  PROD_MODE,
} from '@app/constants';
import { handleCatchError } from '@app/services/__global__/handleCatchError';
import type { NServices } from '@app/types';
import { setAccessToken } from '@app/utils/accessTokenStorage';
import { clog } from '@app/utils/clog';
import { defineDeployingMode } from '@app/utils/defineDeployingMode';

type AuthenticateByCookieResult = Awaited<
  ReturnType<NServices.RedirectToAuthorization>
>;

const SERVER_UNAVAILABLE_STATUSES = [502, 503, 504];

// Бэкенд недоступен на время установки обновления (issue/11376): ответа нет
// совсем (любой код axios: ERR_NETWORK, ECONNABORTED и ETIMEDOUT при таймауте
// соединения и другие) или nginx отвечает 502/503/504. Ответ бэкенда с данными
// (500 с JSON, 401 и любой другой) — это отказ, а не недоступность.
const isServerUnavailableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;

  if (!error.response) return true;

  return SERVER_UNAVAILABLE_STATUSES.includes(error.response.status);
};

// Заглушка nginx может прийти и со статусом 200: вместо JSON — HTML-страница.
const isHtmlResponse = (headers: unknown): boolean => {
  if (!headers || typeof headers !== 'object') return false;

  const contentType = (headers as Record<string, unknown>)['content-type'];

  return typeof contentType === 'string' && contentType.includes('text/html');
};

let isAuthenticating = false; // Флаг для отслеживания процесса аутентификации

let authPromise: Promise<AuthenticateByCookieResult> | undefined = undefined; // Промис для ожидания аутентификации

export const authenticateByCookie: NServices.RedirectToAuthorization =
  async () => {
    if (isAuthenticating && authPromise) {
      return authPromise;
    }

    isAuthenticating = true;
    authPromise = (async (): Promise<AuthenticateByCookieResult> => {
      try {
        const authCookieName =
          defineDeployingMode(window.location.host) === PROD_MODE
            ? BY_HELP_AUTH
            : DEV_BY_HELP_AUTH;
        const authCookie = decodeURI(Cookies.get(authCookieName) || '');

        if (authCookie && authCookie.length > 0) {
          const response = await axios({
            method: POST,
            url: AUTHENTICATE_BY_COOKIE_ENDPOINT,
            data: { Content: authCookie },
          });

          if (isHtmlResponse(response.headers)) {
            return AUTH_SERVER_UNAVAILABLE;
          }

          const result = await response.data;
          const { JsonData } = result;
          const parsedJson = JSON.parse(JsonData);

          if (parsedJson) {
            const { AccessToken } = parsedJson;

            setAccessToken(AccessToken);

            return parsedJson;
          } else {
            return 'navigation';
          }
        } else {
          return 'navigation';
        }
      } catch (error) {
        // Баннер здесь не включается: функцию без токена вызывают и общие
        // запросы API (через customBaseQuery и обёртку старого слоя), для них
        // действует порог интерцептора axios. Баннер включает вызывающий
        // (usePage).
        if (isServerUnavailableError(error)) {
          return AUTH_SERVER_UNAVAILABLE;
        }

        clog('error', handleCatchError(error));

        return 'navigation';
      } finally {
        isAuthenticating = false;
        authPromise = undefined;
      }
    })();

    return authPromise;
  };
